/**
 * Client-side image watermarking via Canvas API.
 *
 * Used before uploading a check-in selfie so the photo carries the
 * date/time/employee/event-kind overlay baked into the image itself
 * (rather than only as DB metadata that could drift).
 *
 * Output is a JPEG blob at 0.85 quality. Image dimensions are kept;
 * a semi-transparent text box sits in the bottom-left.
 */

export interface WatermarkOptions {
  source: Blob;
  /** Each entry becomes its own line in the overlay. */
  lines: string[];
  /** CSS font-family stack. Defaults to the Prompt stack the LIFF uses. */
  fontFamily?: string;
  /** Quality 0..1 for JPEG output. */
  quality?: number;
}

async function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('image decode failed'));
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function watermarkImage(opts: WatermarkOptions): Promise<Blob> {
  const img = await blobToImage(opts.source);
  const canvas = document.createElement('canvas');

  // Cap max dimension to keep upload size reasonable; preserve aspect
  const MAX_SIDE = 1600;
  const ratio = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
  canvas.width = Math.round(img.width * ratio);
  canvas.height = Math.round(img.height * ratio);

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context not available');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const fontSize = Math.max(20, Math.round(canvas.width / 28));
  const padding = Math.round(fontSize * 0.6);
  const lineHeight = Math.round(fontSize * 1.3);
  const fontFamily = opts.fontFamily ?? 'Prompt, system-ui, sans-serif';
  ctx.font = `600 ${fontSize}px ${fontFamily}`;

  // Compute box bounds
  const lineWidths = opts.lines.map((l) => ctx.measureText(l).width);
  const textW = Math.max(...lineWidths, 0);
  const boxW = textW + padding * 2;
  const boxH = opts.lines.length * lineHeight + padding * 2 - (lineHeight - fontSize);
  const boxX = padding;
  const boxY = canvas.height - boxH - padding;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.62)';
  ctx.fillRect(boxX, boxY, boxW, boxH);

  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'top';
  for (let i = 0; i < opts.lines.length; i++) {
    const line = opts.lines[i];
    if (!line) continue;
    ctx.fillText(line, boxX + padding, boxY + padding + i * lineHeight);
  }

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))),
      'image/jpeg',
      opts.quality ?? 0.85,
    );
  });
}
